import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  type AppendMessage,
  AssistantRuntimeProvider,
  type ThreadMessageLike,
  useExternalStoreRuntime,
} from "@assistant-ui/react";
import {
  QueryClient,
  QueryClientProvider,
  useQuery,
  useQueryClient,
  useInfiniteQuery,
} from "@tanstack/react-query";

import { TooltipProvider } from "@penmate/react-shared/ui/tooltip";
import { ShadowPortalProvider } from "@penmate/react-shared/ui/portal";
import { Thread } from "@/components/assistant-ui/thread";
import { NewMessengerIntroDialog, useNewMessengerIntro } from "@/components/assistant-ui/NewMessengerIntroDialog";
import { fetchMessagesPage, type RawMessage } from "@/api/messages";
import { fetchDrafts } from "@/api/drafts";
import { fetchAuthenticatedUser, type PmUser } from "@/api/user";
import { fetchLetter } from "@/api/letters";
import { normalizeThreadEvents, type ThreadEvent } from "@/domain/threadEvent";
import { PhotoViewerProvider } from "@/components/photos/PhotoViewerProvider";
import { trackMetric } from "@/api/metrics";
import { buildPenmateMetricsPayload } from "@/lib/metricsPayload";
import { maybeStartJourneyReplay, stopAllReplays } from "@/lib/posthogReplay";

type ExistingDraft = {
  id: string;
  type: "letter" | "postcard";
  raw: RawMessage;
};

export type PmReactMessagingSeedMessage = {
  id: string;
  text: string;
  direction: "in" | "out";
};

export type PmReactNavigateIntent =
  | { to: "add-credits"; penmateId: string; letterId?: string; returnTo: string }
  | { to: "tracking"; penmateId: string; messageId: string; returnTo: string }
  | { to: "needs-info"; penmateId: string; messageId: string; returnTo: string }
  | { to: "legacy-message"; penmateId: string; messageId: string; emessage?: boolean; returnTo: string };

export type PmReactEvent =
  | { type: "thread.opened"; penmateId: string; messageType?: string; letterId?: string }
  | { type: "thread.updated"; penmateId: string }
  | { type: "thread.sent"; penmateId: string; text: string };

export type PmReactPenmate = {
  id?: string | number;
  first_name?: string | null;
  last_name?: string | null;
  name?: string | null;
  slug?: string | null;
  facility?: string | null;
  [key: string]: unknown;
};

export type PmReactMessagingProps = {
  // Live (React-fetch) mode:
  penmateId?: string;
  penmate?: PmReactPenmate;
  messageType?: string;
  letterId?: string;
  refreshKey?: number;
  checkoutDisabled?: boolean;
  sessionId?: number;
  onNavigate?: (intent: PmReactNavigateIntent) => void;
  onEvent?: (event: PmReactEvent) => void;

  // Sandbox/dev mode (no API calls):
  seedMessages?: readonly PmReactMessagingSeedMessage[];
  onSend?: (text: string) => void; // legacy PoC callback (temporary)
};

const toThreadMessages = (
  messages: readonly PmReactMessagingSeedMessage[],
): readonly ThreadMessageLike[] => {
  return messages.map((m) => ({
    role: m.direction === "out" ? "user" : "assistant",
    content: [{ type: "text", text: m.text }],
    metadata: { custom: { pmMessageId: m.id } },
  }));
};

const toServerThreadMessages = (
  items: readonly RawMessage[],
): ThreadMessageLike[] => {
  const events = normalizeThreadEvents(items);
  return events.map((evt) => ({
    role: evt.role,
    content: [{ type: "text", text: evt.text || "(no content)" }],
    metadata: {
      custom: {
        pmMessageId: evt.id,
        sortTs: evt.sortTs,
        sortTsSecondary: evt.sortTsSecondary,
        pmEvent: evt satisfies ThreadEvent,
      },
    },
  }));
};

const parseDateToTs = (value: unknown): number => {
  if (typeof value !== "string" || !value) return 0;
  const ts = Date.parse(value);
  return Number.isFinite(ts) ? ts : 0;
};

const getDraftType = (raw: any): "letter" | "postcard" => {
  const t =
    (typeof raw?.data?.messageType === "string" ? raw.data.messageType : "") ||
    (typeof raw?.order_type === "string" ? raw.order_type : "");
  return String(t).toLowerCase() === "postcard" ? "postcard" : "letter";
};

const getLetterIdFromSearch = (search: string): string | null => {
  try {
    const s = typeof search === "string" ? search : "";
    const params = new URLSearchParams(s.startsWith("?") ? s.slice(1) : s);
    const v = params.get("letterId") || params.get("messageId");
    return v && v.trim() ? v.trim() : null;
  } catch {
    return null;
  }
};

const useLocationLetterId = (): string | null => {
  const [value, setValue] = useState<string | null>(() => {
    if (typeof window === "undefined") return null;
    return getLetterIdFromSearch(window.location.search);
  });

  useEffect(() => {
    if (typeof window === "undefined") return;

    const install = () => {
      const w = window as any;
      if (w.__pmreact_locationchange_installed) return;
      w.__pmreact_locationchange_installed = true;

      const dispatch = () => {
        try {
          window.dispatchEvent(new Event("pmreact:locationchange"));
        } catch {
          // no-op
        }
      };

      const historyObj = window.history as any;
      const wrap = (fnName: "pushState" | "replaceState") => {
        const original = historyObj[fnName];
        if (typeof original !== "function") return;
        historyObj[fnName] = function (...args: any[]) {
          const ret = original.apply(this, args);
          dispatch();
          return ret;
        };
      };

      wrap("pushState");
      wrap("replaceState");
      window.addEventListener("popstate", dispatch);
      window.addEventListener("hashchange", dispatch);
    };

    install();

    const onChange = () => {
      setValue(getLetterIdFromSearch(window.location.search));
    };
    window.addEventListener("pmreact:locationchange", onChange);
    return () => {
      window.removeEventListener("pmreact:locationchange", onChange);
    };
  }, []);

  return value;
};

const getRawStatusLower = (raw: any): string | null => {
  const state = raw?.status ?? raw?.data?.status;
  return typeof state === "string" && state.trim() ? state.trim().toLowerCase() : null;
};

const isRawDraftByStatus = (raw: any): boolean => {
  return getRawStatusLower(raw) === "draft";
};

const getLetterPenmateId = (raw: any): string | null => {
  const v =
    raw?.recipient?.id ??
    raw?.inmate?.id ??
    raw?.penmate?.id ??
    raw?.inmate_id ??
    raw?.data?.recipient?.id ??
    raw?.data?.inmate?.id ??
    raw?.data?.penmate?.id ??
    raw?.data?.inmate_id ??
    raw?.data?.penmate_id;
  if (typeof v === "number" && Number.isFinite(v)) return String(v);
  if (typeof v === "string" && v.trim()) return v.trim();
  return null;
};

const isRawDraft = (raw: any): boolean => {
  const state = raw?.status ?? raw?.data?.status;
  return typeof state === "string" && state === "draft";
};

const pickMostRecentDraft = (drafts: readonly RawMessage[]): RawMessage | null => {
  let best: RawMessage | null = null;
  let bestTs = -1;
  for (const d of drafts) {
    const ts =
      parseDateToTs((d as any)?.updated_at) ||
      parseDateToTs((d as any)?.created_at) ||
      parseDateToTs((d as any)?.timestamp);
    if (ts > bestTs) {
      best = d;
      bestTs = ts;
    }
  }
  return best;
};

const getExistingDraftsByType = (
  items: readonly RawMessage[],
): { letter: ExistingDraft | null; postcard: ExistingDraft | null } => {
  const drafts = items.filter((r) => isRawDraft(r));
  const letter = pickMostRecentDraft(drafts.filter((r) => getDraftType(r) === "letter"));
  const postcard = pickMostRecentDraft(drafts.filter((r) => getDraftType(r) === "postcard"));
  return {
    letter: letter ? { id: String((letter as any).id), type: "letter", raw: letter } : null,
    postcard: postcard ? { id: String((postcard as any).id), type: "postcard", raw: postcard } : null,
  };
};

const getExistingDraftsByTypeFromRuntime = (
  threadMessages: readonly ThreadMessageLike[],
): { letter: ExistingDraft | null; postcard: ExistingDraft | null } => {
  let letter: ExistingDraft | null = null;
  let postcard: ExistingDraft | null = null;
  let bestLetterTs = -1;
  let bestPostcardTs = -1;

  for (const m of threadMessages) {
    const anyMsg = m as any;
    const pmEvent = anyMsg?.metadata?.custom?.pmEvent as ThreadEvent | undefined;
    if (!pmEvent) continue;
    if (!isRawDraft(pmEvent.raw)) continue;
    const type = getDraftType(pmEvent.raw);
    const ts = typeof pmEvent.sortTs === "number" ? pmEvent.sortTs : 0;
    const id = pmEvent.id;
    const raw = pmEvent.raw as RawMessage;
    if (type === "letter") {
      if (ts > bestLetterTs) {
        bestLetterTs = ts;
        letter = { id, type: "letter", raw };
      }
    } else {
      if (ts > bestPostcardTs) {
        bestPostcardTs = ts;
        postcard = { id, type: "postcard", raw };
      }
    }
  }

  return { letter, postcard };
};

const getPmMessageId = (msg: ThreadMessageLike): string | null => {
  const anyMsg = msg as any;
  const id = anyMsg && anyMsg.metadata && anyMsg.metadata.custom && anyMsg.metadata.custom.pmMessageId;
  return id ? String(id) : null;
};

const getThreadMessageSortTs = (msg: ThreadMessageLike): number => {
  const anyMsg = msg as any;
  const ts = anyMsg && anyMsg.metadata && anyMsg.metadata.custom && anyMsg.metadata.custom.sortTs;
  return typeof ts === "number" ? ts : Number.POSITIVE_INFINITY;
};

const getThreadMessageSortTsSecondary = (msg: ThreadMessageLike): number => {
  const anyMsg = msg as any;
  const ts =
    anyMsg &&
    anyMsg.metadata &&
    anyMsg.metadata.custom &&
    anyMsg.metadata.custom.sortTsSecondary;
  if (typeof ts === "number") return ts;
  return getThreadMessageSortTs(msg);
};

const mergeServerIntoThread = (
  current: readonly ThreadMessageLike[],
  server: readonly ThreadMessageLike[],
): readonly ThreadMessageLike[] => {
  const locals = current.filter((m) => !getPmMessageId(m));
  return server
    .concat(locals)
    .sort((a, b) => {
      const a1 = getThreadMessageSortTs(a);
      const b1 = getThreadMessageSortTs(b);
      if (a1 !== b1) return a1 - b1;
      const a2 = getThreadMessageSortTsSecondary(a);
      const b2 = getThreadMessageSortTsSecondary(b);
      if (a2 !== b2) return a2 - b2;
      const aId = getPmMessageId(a) || "";
      const bId = getPmMessageId(b) || "";
      return aId.localeCompare(bId);
    });
};

function PmReactMessagingInner(props: PmReactMessagingProps) {
  const {
    penmateId,
    penmate,
    messageType,
    letterId,
    refreshKey,
    checkoutDisabled = false,
    sessionId,
    seedMessages = [],
    onNavigate,
    onEvent,
    onSend,
  } = props;

  const isLive = !!penmateId;
  const queryClient = useQueryClient();
  const [checkoutDraftId, setCheckoutDraftId] = useState<string | null>(null);
  const [checkoutDraftSource, setCheckoutDraftSource] = useState<"ui" | "deeplink" | null>(null);
  const [checkoutEntryNotice, setCheckoutEntryNotice] = useState<string | null>(null);
  const consumedCheckoutIdRef = useRef<string | null>(null);
  const locationLetterId = useLocationLetterId();
  const effectiveLetterId = letterId || locationLetterId || undefined;
  const lastRefreshKeyRef = useRef<number | undefined>(refreshKey);
  const [optimisticThreadItems, setOptimisticThreadItems] = useState<readonly RawMessage[]>([]);
  const deepLinkThreadRefreshRef = useRef<string | null>(null);

  useEffect(() => {
    if (typeof window === "undefined") return;
    // The host page scopes its leave interception (checkout recovery) to an
    // active checkout; publish every change, and clear on unmount so a
    // destroyed messenger never leaves a stale "active" behind.
    const publish = (draftId: string | null) => {
      (window as any).__pmreact_checkout_draft_id = draftId;
      try {
        window.dispatchEvent(new CustomEvent("pmreact:checkout_active_changed", { detail: { draftId } }));
      } catch {
        // no-op
      }
    };
    publish(checkoutDraftId || null);
    return () => publish(null);
  }, [checkoutDraftId]);

  useEffect(() => {
    if (!isLive) return;
    if (!checkoutDisabled) return;
    if (!checkoutDraftId) return;
    setCheckoutDraftId(null);
    setCheckoutDraftSource(null);
    setCheckoutEntryNotice(null);
  }, [checkoutDisabled, checkoutDraftId, isLive]);

  useEffect(() => {
    if (!isLive) return;
    if (checkoutDisabled) return;
    const handler = (evt: Event) => {
      const anyEvt = evt as CustomEvent | null;
      const detail = anyEvt && typeof (anyEvt as any).detail === "object" ? (anyEvt as any).detail : null;
      const draftId = detail && detail.draftId ? String(detail.draftId) : "";
      const targetPenmateId = detail && detail.penmateId ? String(detail.penmateId) : "";
      if (!draftId) return;
      if (targetPenmateId && penmateId && targetPenmateId !== String(penmateId)) return;

      consumedCheckoutIdRef.current = draftId;
      setCheckoutDraftSource("ui");
      setCheckoutEntryNotice(null);
      setCheckoutDraftId(draftId);
    };

    window.addEventListener("pmreact:checkout_started", handler);
    return () => {
      window.removeEventListener("pmreact:checkout_started", handler);
    };
  }, [checkoutDisabled, isLive, penmateId]);

  useEffect(() => {
    if (!isLive) return;
    if (checkoutDisabled) return;
    if (checkoutDraftId) return;
    if (typeof effectiveLetterId !== "string" || !effectiveLetterId.trim()) return;
    const next = effectiveLetterId.trim();
    if (consumedCheckoutIdRef.current === next) return;
    consumedCheckoutIdRef.current = next;
    setCheckoutDraftSource("deeplink");
    setCheckoutEntryNotice(null);
    setCheckoutDraftId(next);
  }, [checkoutDisabled, isLive, checkoutDraftId, effectiveLetterId]);

  const [messages, setMessages] = useState<readonly ThreadMessageLike[]>(() =>
    seedMessages.length ? toThreadMessages(seedMessages) : [],
  );
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);

  const viewportRef = useRef<HTMLDivElement | null>(null);
  const isMountedRef = useRef(false);
  const scrollRestoreRef = useRef<{ height: number; top: number } | null>(null);

  useEffect(() => {
    if (!isLive || !penmateId) return;
    onEvent?.({ type: "thread.opened", penmateId, messageType, letterId });
    const penmateMetrics = buildPenmateMetricsPayload({ penmate, penmateId });
    void trackMetric("messenger-v2-opened", {
      ...penmateMetrics,
      message_type: messageType || null,
      letter_id: effectiveLetterId || null,
    }, { sessionId });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLive, penmateId]);

  const query = useInfiniteQuery({
    queryKey: ["pmreact", "messaging", "thread", { penmateId, messageType }],
    enabled: isLive,
    initialPageParam: undefined as string | undefined,
    queryFn: ({ pageParam }) =>
      fetchMessagesPage({ penmateId: penmateId!, cursor: pageParam }),
    getNextPageParam: (lastPage) =>
      lastPage.hasNextPage ? lastPage.nextCursor || undefined : undefined,
    staleTime: 30_000,
  });

  const draftsQuery = useQuery<RawMessage[]>({
    queryKey: ["pmreact", "messaging", "drafts"],
    queryFn: fetchDrafts,
    enabled: isLive,
    staleTime: 30_000,
  });

  useEffect(() => {
    if (!isLive) return;
    if (refreshKey === undefined || refreshKey === null) return;
    if (lastRefreshKeyRef.current === refreshKey) return;
    lastRefreshKeyRef.current = refreshKey;
    void queryClient.invalidateQueries({
      queryKey: ["pmreact", "messaging", "thread", { penmateId, messageType }],
      exact: true,
    });
    void queryClient.invalidateQueries({ queryKey: ["pmreact", "messaging", "drafts"], exact: true });
  }, [isLive, messageType, penmateId, queryClient, refreshKey]);

  const penmateDraftItems = useMemo(() => {
    if (!isLive) return [];
    if (!penmateId) return [];
    const items = draftsQuery.data || [];
    const target = String(penmateId);
    return items.filter((i) => getLetterPenmateId(i) === target);
  }, [draftsQuery.data, isLive, penmateId]);

  useEffect(() => {
    if (!isLive) return;
    if (!checkoutDraftId) return;
    const viaDeepLink = typeof effectiveLetterId === "string" && effectiveLetterId.trim() === checkoutDraftId;
    if (!viaDeepLink) return;
    if (deepLinkThreadRefreshRef.current === checkoutDraftId) return;
    deepLinkThreadRefreshRef.current = checkoutDraftId;

    // If checkout was entered via a deep-link (e.g. resend), refresh the thread in the background so
    // the newly-created draft appears in history.
    void queryClient.invalidateQueries({ queryKey: ["pmreact", "messaging", "thread"], exact: false });
  }, [checkoutDraftId, effectiveLetterId, isLive, queryClient]);

  const authUserQuery = useQuery<PmUser>({
    queryKey: ["pmreact", "auth", "validate-token"],
    queryFn: fetchAuthenticatedUser,
    // Fetch early so checkout can render immediately without waiting for a fresh user/credit load.
    enabled: isLive && !checkoutDisabled,
    staleTime: 30_000,
  });

  const checkoutLetterQuery = useQuery<RawMessage>({
    queryKey: ["pmreact", "letters", "by-id", { letterId: checkoutDraftId }],
    queryFn: () => fetchLetter({ letterId: checkoutDraftId! }),
    enabled: isLive && !!checkoutDraftId && !checkoutDisabled,
    staleTime: 5_000,
  });

  // Checkout always prices from a FRESH read: the server computes every
  // pricing and promo fact on read, and a letter cached earlier in this
  // page's life (a tab kept alive across a server change did this) must
  // never price a checkout. A first entry is fetching anyway; a re-entry
  // with cached data refetches before the card trusts it.
  useEffect(() => {
    if (!isLive || !checkoutDraftId || checkoutDisabled) return;
    const queryKey = ["pmreact", "letters", "by-id", { letterId: checkoutDraftId }];
    if (queryClient.getQueryState(queryKey)?.data !== undefined) {
      void queryClient.invalidateQueries({ queryKey });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checkoutDraftId, checkoutDisabled, isLive]);

  useEffect(() => {
    if (!isLive) return;
    if (checkoutDisabled) {
      stopAllReplays();
      return;
    }
    if (!authUserQuery.data) return;
    maybeStartJourneyReplay(authUserQuery.data);
  }, [authUserQuery.data, checkoutDisabled, isLive]);

  useEffect(() => {
    if (!isLive) return;
    if (!checkoutDraftId) return;
    if (checkoutDisabled) {
      setCheckoutDraftId(null);
      setCheckoutDraftSource(null);
      setCheckoutEntryNotice(null);
      return;
    }
    if (!checkoutLetterQuery.isSuccess) return;

    const letter = checkoutLetterQuery.data;
    const id = String((letter as any)?.id ?? checkoutDraftId);
    const statusLower = getRawStatusLower(letter);
    const isDraft = statusLower ? statusLower === "draft" : true;
    if (!isDraft) return;

    const fromThread =
      !!query.data &&
      query.data.pages.some((p) =>
        p.items.some((i) => String((i as any)?.id ?? "") === id),
      );

    setOptimisticThreadItems((prev) => {
      const already = prev.some((i) => String((i as any)?.id ?? "") === id);
      if (fromThread) return already ? prev.filter((i) => String((i as any)?.id ?? "") !== id) : prev;
      if (already) return prev;
      return prev.concat(letter);
    });
  }, [checkoutDraftId, checkoutLetterQuery.data, checkoutLetterQuery.isSuccess, isLive, query.data]);

  useEffect(() => {
    if (!isLive) return;
    if (!checkoutDraftId) return;
    if (!checkoutLetterQuery.isSuccess) return;

    const letter = checkoutLetterQuery.data;
    const statusLower = getRawStatusLower(letter);
    const isDraft = statusLower ? statusLower === "draft" : true;
    const rawPenmateId = getLetterPenmateId(letter);
    const matchesPenmate =
      !rawPenmateId || !penmateId ? true : String(rawPenmateId) === String(penmateId);

    if (isDraft && matchesPenmate) return;

    setCheckoutDraftId(null);
    setCheckoutDraftSource(null);
    // Only show an entry notice when checkout was entered via an explicit deep-link.
    // (When a user sends a message via the in-app checkout flow, we silently exit checkout.)
    const shouldShowNotice = checkoutDraftSource === "deeplink";
    setCheckoutEntryNotice(
      shouldShowNotice
        ? !matchesPenmate
          ? "Unable to open checkout for that message."
          : "This message has already been sent. Checkout isn’t available."
        : null,
    );
  }, [
    checkoutDisabled,
    checkoutDraftId,
    checkoutDraftSource,
    checkoutLetterQuery.data,
    checkoutLetterQuery.isSuccess,
    isLive,
    penmateId,
  ]);

  useEffect(() => {
    if (!isLive) return;
    setHasLoadedOnce(false);
  }, [isLive, penmateId, messageType]);

  useEffect(() => {
    if (!query.data) return;
    const serverIds = new Set<string>();
    for (const p of query.data.pages) {
      for (const i of p.items) serverIds.add(String((i as any)?.id ?? ""));
    }
    for (const i of penmateDraftItems) {
      serverIds.add(String((i as any)?.id ?? ""));
    }
    if (!serverIds.size) return;
    setOptimisticThreadItems((prev) =>
      prev.filter((i) => !serverIds.has(String((i as any)?.id ?? ""))),
    );
  }, [penmateDraftItems, query.data]);

  useEffect(() => {
    if (!isLive) return;
    if (query.isSuccess || query.isError) setHasLoadedOnce(true);
  }, [isLive, query.isError, query.isSuccess]);

  const serverThreadMessages = useMemo(() => {
    if (!query.data) return [];
    const allItems = query.data.pages.flatMap((p) => p.items);
    if (!optimisticThreadItems.length && !penmateDraftItems.length) {
      return toServerThreadMessages(allItems);
    }

    const merged = new Map<string, RawMessage>();
    for (const item of allItems) merged.set(String((item as any)?.id ?? ""), item);
    for (const item of penmateDraftItems) {
      const id = String((item as any)?.id ?? "");
      if (!merged.has(id)) merged.set(id, item);
    }
    for (const item of optimisticThreadItems) {
      const id = String((item as any)?.id ?? "");
      if (!merged.has(id)) merged.set(id, item);
    }
    return toServerThreadMessages(Array.from(merged.values()));
  }, [optimisticThreadItems, penmateDraftItems, query.data]);

  const handleResendDraftDeleted = useCallback((draftId: string) => {
    if (!draftId) return;
    setOptimisticThreadItems((prev) =>
      prev.filter((item) => String((item as any)?.id ?? "") !== draftId),
    );
  }, []);

  const existingDraftsByType = useMemo(() => {
    const fromRuntime = getExistingDraftsByTypeFromRuntime(messages);
    if (!query.data) return fromRuntime;
    const allItems = query.data.pages.flatMap((p) => p.items).concat(penmateDraftItems);
    const fromApi = getExistingDraftsByType(allItems);
    return {
      letter: fromApi.letter || fromRuntime.letter,
      postcard: fromApi.postcard || fromRuntime.postcard,
    };
  }, [messages, penmateDraftItems, query.data]);

  useEffect(() => {
    if (!isLive) return;
    setMessages((current) => mergeServerIntoThread(current, serverThreadMessages));
    if (penmateId) onEvent?.({ type: "thread.updated", penmateId });
  }, [isLive, penmateId, onEvent, serverThreadMessages]);

  useEffect(() => {
    const viewport = viewportRef.current;
    if (!viewport) return;
    if (isMountedRef.current) return;
    if (!messages.length) return;
    isMountedRef.current = true;
    requestAnimationFrame(() => {
      viewport.scrollTop = viewport.scrollHeight;
    });
  }, [messages.length]);

  useEffect(() => {
    const viewport = viewportRef.current;
    const restore = scrollRestoreRef.current;
    if (!viewport || !restore) return;
    // After older messages load, keep the current content pinned.
    requestAnimationFrame(() => {
      const delta = viewport.scrollHeight - restore.height;
      viewport.scrollTop = restore.top + delta;
      scrollRestoreRef.current = null;
    });
  }, [serverThreadMessages.length]);

  const onNew = async (message: AppendMessage) => {
    const textPart = message.content.find(
      (p): p is { type: "text"; text: string } => p.type === "text",
    );
    const text = (textPart && textPart.text) || "";
    if (!text.trim()) return;
    onSend?.(text);
    if (penmateId) onEvent?.({ type: "thread.sent", penmateId, text });

    const now = Date.now();
    const userMessage: ThreadMessageLike = {
      role: "user",
      content: [{ type: "text", text }],
      metadata: { custom: { sortTs: now, sortTsSecondary: now } },
    };
    setMessages((current) => current.concat(userMessage));

    const systemMessage: ThreadMessageLike = {
      role: "system",
      content: [{ type: "text", text: "Sent (mock). No server yet." }],
      metadata: { custom: { sortTs: now + 1, sortTsSecondary: now + 1 } },
    };
    setMessages((current) => current.concat(systemMessage));
  };

  const runtime = useExternalStoreRuntime<ThreadMessageLike>({
    messages,
    setMessages,
    onNew,
    convertMessage: (m) => m,
  });

  const onViewportScroll = (e: React.UIEvent<HTMLDivElement>) => {
    if (!isLive) return;
    if (!query.hasNextPage || query.isFetchingNextPage) return;
    const target = e.currentTarget;
    if (target.scrollTop > 100) return;
    scrollRestoreRef.current = { height: target.scrollHeight, top: target.scrollTop };
    query.fetchNextPage().catch(() => {
      scrollRestoreRef.current = null;
    });
  };

  const emptyState: "loading" | "empty" | "none" =
    messages.length > 0
      ? "none"
      : isLive
        ? !hasLoadedOnce && query.isFetching
          ? "loading"
          : hasLoadedOnce
            ? "empty"
            : "loading"
        : "empty";

  const hasIncomingReplies = useMemo(() => {
    if (!isLive) return false;
    const pages = query.data?.pages ?? [];
    if (!pages.length) return false;
    const items = pages.flatMap((p) => p.items ?? []);
    const events = normalizeThreadEvents(items);
    return events.some((e) => e.role === "assistant");
  }, [isLive, query.data]);

  const intro = useNewMessengerIntro({
    enabled: isLive && !checkoutDisabled && !checkoutDraftId && emptyState !== "loading",
    user: authUserQuery.data || null,
  });

  return (
    <div className="pm-react-root">
      <div className="h-full w-full">
        <ShadowPortalProvider>
          <TooltipProvider>
            <PhotoViewerProvider>
              <AssistantRuntimeProvider runtime={runtime}>
                <NewMessengerIntroDialog open={intro.open} onDismiss={intro.dismiss} />
                <Thread
                  viewportRef={viewportRef}
                  onViewportScroll={onViewportScroll}
                  emptyState={emptyState}
                  messageType={messageType}
                  existingDraftsByType={existingDraftsByType}
                  penmateId={penmateId}
                  penmate={penmate}
                  sessionId={sessionId}
                  onNavigate={onNavigate}
                  hasIncomingReplies={hasIncomingReplies}
                  checkoutDisabled={checkoutDisabled}
                  checkoutDraftId={checkoutDraftId}
                  draftsLoading={draftsQuery.isLoading}
                  checkoutEntryNotice={checkoutEntryNotice}
                  checkoutUser={authUserQuery.data || null}
                  checkoutUserLoading={authUserQuery.isFetching}
                  checkoutLetter={checkoutLetterQuery.data || null}
                  checkoutLetterLoading={checkoutLetterQuery.isFetching}
                  onEnterCheckout={(draftId) => {
                    if (checkoutDisabled) return;
                    setCheckoutDraftSource("ui");
                    setCheckoutEntryNotice(null);
                    setCheckoutDraftId(draftId);
                  }}
                  onCancelCheckout={() => {
                    setCheckoutDraftId(null);
                    setCheckoutDraftSource(null);
                    setCheckoutEntryNotice(null);
                  }}
                  onResendDraftDeleted={handleResendDraftDeleted}
                />
              </AssistantRuntimeProvider>
            </PhotoViewerProvider>
          </TooltipProvider>
        </ShadowPortalProvider>
      </div>
    </div>
  );
}

export function PmReactMessagingApp(props: PmReactMessagingProps) {
  const queryClient = useMemo(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: { retry: 1, refetchOnWindowFocus: false },
        },
      }),
    [],
  );

  return (
    <QueryClientProvider client={queryClient}>
      <PmReactMessagingInner {...props} />
    </QueryClientProvider>
  );
}

// Test-only exports (keep runtime logic in this file for now).
export const __test__ = {
  getDraftType,
  isRawDraft,
  isRawDraftByStatus,
  getRawStatusLower,
  getLetterPenmateId,
  pickMostRecentDraft,
  getExistingDraftsByType,
  mergeServerIntoThread,
};
